import { useState } from "react";
import { Link } from "react-router-dom";
import CompatibilitySearch from "@/components/CompatibilitySearch";
import AlientechInstantSearch from "@/components/AlientechInstantSearch";
import MagicMotorsportInstantSearch from "@/components/MagicMotorsportInstantSearch";
import DynoecuInstantSearch from "@/components/DynoecuInstantSearch";
import UnifiedSearch from "@/components/UnifiedSearch";
import MethodGlyph from "@/components/MethodGlyph";
import EcuBrandLogo, { ecuBrandLogoAvailable } from "@/components/EcuBrandLogo";
import ToolLogo from "@/components/ToolLogo";

// Routed through our own backend (same-origin HTTPS) instead of hitting the
// Meilisearch host directly — it's plain HTTP-only, which browsers block as
// mixed content. Proxy per provider so each ships its own scoped search key
// server-side: /api/meili -> Autotuner index, /api/alientech/meili ->
// Alientech index, /api/magicmotorsport/meili -> MagicMotorsport index.
// Built from window.location.origin (not the API helper/
// REACT_APP_BACKEND_URL) because that's often "" in production
// (frontend+backend are same-origin), and the Meilisearch client needs an
// absolute URL, not a relative path.
const MEILI_HOST = `${window.location.origin}/api/meili`;
const ALIENTECH_MEILI_HOST = `${window.location.origin}/api/alientech/meili`;
const MAGICMOTORSPORT_MEILI_HOST = `${window.location.origin}/api/magicmotorsport/meili`;
const DYNOECU_MEILI_HOST = `${window.location.origin}/api/dynoecu/meili`;

const PROVIDER_LOGO_SIZES = {
  autotuner: "h-6",
  alientech: "h-3.5",
  magicmotorsport: "h-6",
};

function ProviderTab({ active, onClick, children, testid, logo }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition ${
        active
          ? "bg-[#D32222] text-white"
          : "text-neutral-400 hover:text-neutral-100 hover:bg-neutral-800/60"
      }`}
      aria-label={typeof children === "string" ? children : undefined}
    >
      {logo && (
        <ToolLogo source={logo} className={PROVIDER_LOGO_SIZES[logo] || "h-4"} />
      )}
      {!logo && children}
    </button>
  );
}

export default function InstantSearchPage() {
  const [provider, setProvider] = useState("autotuner");

  return (
    <div data-testid="instant-search-page" className="space-y-6">
      <div>
        <h1
          className="text-4xl sm:text-5xl leading-[0.95] tracking-tight"
          style={{ fontFamily: "'Orbitron', sans-serif" }}
        >
          Sofortsuche über <span className="text-[#D32222]">alle Fahrzeuge</span>
        </h1>
        <p className="mt-4 text-neutral-400 max-w-2xl text-sm sm:text-base">
          Tippgeschwindigkeit statt Server-Roundtrip — durchsucht denselben
          Datensatz wie die jeweilige Kompatibilitätsliste, aber über einen
          separaten Meilisearch-Index mit Facetten-Filtern.
        </p>
      </div>

      <div className="inline-flex items-center gap-1 rounded-lg border border-neutral-800 bg-panel p-1">
        <ProviderTab
          active={provider === "alle"}
          onClick={() => setProvider("alle")}
          testid="search-tab-alle"
        >
          Alle
        </ProviderTab>
        <ProviderTab
          active={provider === "autotuner"}
          onClick={() => setProvider("autotuner")}
          testid="search-tab-autotuner"
          logo="autotuner"
        >
          Autotuner
        </ProviderTab>
        <ProviderTab
          active={provider === "alientech"}
          onClick={() => setProvider("alientech")}
          testid="search-tab-alientech"
          logo="alientech"
        >
          Alientech
        </ProviderTab>
        <ProviderTab
          active={provider === "magicmotorsport"}
          onClick={() => setProvider("magicmotorsport")}
          testid="search-tab-magicmotorsport"
          logo="magicmotorsport"
        >
          MagicMotorsport
        </ProviderTab>
        <ProviderTab
          active={provider === "dynoecu"}
          onClick={() => setProvider("dynoecu")}
          testid="search-tab-dynoecu"
          logo="dynoecu"
        >
          Original ECU Dateien
        </ProviderTab>
      </div>

      {provider === "alle" && <UnifiedSearch />}
      {provider === "autotuner" && (
        <CompatibilitySearch
          meiliHost={MEILI_HOST}
          LinkComponent={Link}
          getResultHref={(hit) =>
            hit.compat_id ? `/vehicle/${encodeURIComponent(hit.compat_id)}` : undefined
          }
          renderMethodIcon={(label) => <MethodGlyph label={label} className="w-8 h-8" />}
          renderEcuLogo={(name) =>
            ecuBrandLogoAvailable(name) && (
              <span className="w-6 h-4 flex items-center justify-center shrink-0">
                <EcuBrandLogo name={name} className="max-h-4 max-w-6 w-auto" />
              </span>
            )
          }
        />
      )}
      {provider === "alientech" && (
        <AlientechInstantSearch meiliHost={ALIENTECH_MEILI_HOST} />
      )}
      {provider === "magicmotorsport" && (
        <MagicMotorsportInstantSearch meiliHost={MAGICMOTORSPORT_MEILI_HOST} />
      )}
      {provider === "dynoecu" && (
        <DynoecuInstantSearch meiliHost={DYNOECU_MEILI_HOST} />
      )}
    </div>
  );
}
